import axios from 'axios';
import * as Sentry from '@sentry/react';

const baseURL =
  window.location.hostname === 'healthdeal.com.au'
    ? 'https://api.healthdeal.com.au' // Prod API
    : 'https://hd-api-dev.ap-southeast-2.elasticbeanstalk.com'; // Default API

const axiosRequest = axios.create({
  baseURL: baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token management
let tokenPromise: Promise<string> | null = null;

export const getToken = async () => {
  const sec =
    window.location.hostname === 'healthdeal.com.au'
      ? 'clS9ab7U@=EMu*E'
      : 'cri81ma_7U?emA#';

  const params = {
    client_id: 'smithbrothersmedia',
    client_secret: sec,
  };

  try {
    // Create a separate axios instance for token requests to avoid interceptor loops
    const tokenAxios = axios.create({
      baseURL: baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const tokenResponse = await tokenAxios.post('/getToken', params);
    const newToken = tokenResponse?.data?.token;

    if (!newToken) {
      throw new Error('Token not found in response');
    }

    localStorage.setItem('token', newToken);
    return newToken;
  } catch (error) {
    throw error;
  }
};

// Ensure token is available
const ensureToken = async (): Promise<string> => {
  let token = localStorage.getItem('token');

  if (!token) {
    // Prevent multiple concurrent token requests
    if (!tokenPromise) {
      tokenPromise = getToken();
    }
    token = await tokenPromise;
    tokenPromise = null;
  }

  return token;
};

axiosRequest.interceptors.request.use(
  async (config) => {
    // Skip token for getToken endpoint to avoid circular dependency
    if (config.url === '/getToken') {
      return config;
    }

    try {
      const token = await ensureToken();
      config.headers.Authorization = `Bearer ${token}`;

    } catch (error) {
      
      throw error;
    }

    return config;
  },
);

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

axiosRequest.interceptors.response.use(
  (response) => {

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.message === 'canceled') {
      return Promise.reject(error);
    }

    // Initialize retry count if not already set
    if (!originalRequest._retryCount) {
      originalRequest._retryCount = 0;
    }

    const url = originalRequest?.url || 'unknown';
    const status = error.response?.status;

    // IMPROVED RETRY LOGIC: Only retry errors that make sense to retry
    const shouldRetry = (() => {
      // Network errors (no response) - should retry
      if (!error.response) {
        
        return true;
      }

      // 401 Unauthorized - token might be expired, should retry
      if (status === 401) {
       
        return true;
      }

      // 5xx Server errors - temporary issues, should retry
      if (status >= 500) {
      
        return true;
      }

      // 4xx Client errors (except 401) - validation/client errors, DON'T retry
      if (status >= 400 && status < 500) {
        
        return false;
      }

      // Unknown status - default to retry
      
      return true;
    })();

    if (shouldRetry && originalRequest._retryCount < 3) {
      originalRequest._retryCount += 1;
      try {
        await delay(1000);
        // Clear token cache and get fresh token
        localStorage.removeItem('token');
        const newToken = await getToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosRequest(originalRequest);
      } catch (tokenError) {
        return Promise.reject(error);
      }
    }

    // Log to Sentry with appropriate categorization
    if (!shouldRetry) {
      // Validation/client errors - log without retry noise
      Sentry.captureException(error, {
        tags: {
          'api-request': url,
          'error-type': 'validation-error',
          'status-code': status,
        },
        extra: {
          response: error.response,
          responseData: error.response?.data,
          reason: 'Client error - no retry attempted',
        },
      });
    } else if (originalRequest._retryCount >= 3) {
      // Retry exhausted - different category
      Sentry.captureException(error, {
        tags: {
          'api-request': url,
          'error-type': 'retry-exhausted',
        },
        extra: {
          response: error.response,
          responseData: error.response?.data,
          reason: `Max retry attempts reached for ${url}`,
          retryCount: originalRequest._retryCount,
        },
      });
    }

    return Promise.reject(error);
  },
);

export default axiosRequest;
